import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export const EMPTY_GUID_VALUE = "00000000-0000-0000-0000-000000000000" as const;
export type EmptyGuid = typeof EMPTY_GUID_VALUE;

export type GuidParams = z.infer<typeof GuidSchema.GuidParamsZ>;
export type GuidJSON = z.infer<typeof GuidSchema.GuidJSONZ>;

/**
 * Every `Guid` in the app is checked here, so this decides which ids the browser will read.
 *
 * `z.guid()` rather than `z.uuid()`: Zod 4's `z.uuid()` also checks the RFC 9562 version and variant
 * digits, which a .NET `Guid` never promises -- any 128-bit value is a valid one. The API's own ids
 * all carry them (`Guid.NewGuid()` is v4, Npgsql's key generator v7, `DeterministicId` v5), so the
 * stricter check only ever rejected ids somebody wrote by hand, such as
 * `11111111-1111-1111-1111-111111111111` in a local seed -- and it rejected them by failing the whole
 * response that carried one. `z.guid()` accepts the nil id too, which is why `allowEmpty` needs no
 * union and the default form refines the nil id away.
 */
export default class GuidSchema {
  public static readonly type = z.custom<Guid>();

  public static readonly GuidParamsZ = z
    .guid()
    .refine((val) => val !== "00000000-0000-0000-0000-000000000000", "Nil UUID is not allowed");
  public static readonly GuidJSONZ = z.guid();

  public static validate(values: unknown, allowEmpty: boolean = false): GuidParams {
    const schema = allowEmpty ? z.guid() : GuidSchema.GuidParamsZ;
    return parseOrThrow(schema, values, {
      logError: true,
      schemaName: "GuidSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown, allowEmpty: boolean = false): string {
    const schema = allowEmpty ? z.guid() : GuidSchema.GuidJSONZ;
    return parseOrThrow(schema, json, {
      logError: true,
      schemaName: "GuidSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static serialize(guidValue: string, allowEmpty: boolean = false): string {
    const schema = allowEmpty ? z.guid() : GuidSchema.GuidJSONZ;
    return parseOrThrow(schema, guidValue, {
      logError: true,
      schemaName: "GuidSchema",
      operation: "serialize",
      payloadSample: guidValue,
    });
  }

  public static deserialize(data: unknown, allowEmpty: boolean = false): GuidParams {
    const schema = allowEmpty ? z.guid() : GuidSchema.GuidParamsZ;
    return parseOrThrow(schema, data, {
      logError: true,
      schemaName: "GuidSchema",
      operation: "deserialize",
      payloadSample: data,
    });
  }

  public static deserializeArray(data: unknown, allowEmpty: boolean = false): GuidParams[] {
    const schema = allowEmpty
      ? z.array(z.guid()).nullable()
      : z.array(GuidSchema.GuidParamsZ).nullable();
    return (
      parseOrThrow(schema, data, {
        logError: true,
        schemaName: "GuidSchema",
        operation: "deserializeArray",
        payloadSample: data,
      }) ?? []
    );
  }
}
